/* ===========================================================
   Quill 본문 뷰어 (읽기전용)
   - 기사 상세 content(Quill Delta)를 관리자 에디터와 동일한 Quill 스타일로 렌더
   - Quill 2.x 코어 사용 (react-quill은 React 19에서 findDOMNode 이슈로 미사용)
   - size 가 숫자(px)로 저장된 Delta를 위해 style 기반 attributor 를 허용 등록
   =========================================================== */
import { useEffect, useRef } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";

/* size/align/font 를 인라인 스타일 기반으로 등록(임의 값 허용)
   → 관리자 에디터가 저장한 size:10 같은 px 값도 그대로 반영 */
let registered = false;
function ensureRegistered() {
  if (registered) return;
  try {
    const SizeStyle = Quill.import("attributors/style/size");
    SizeStyle.whitelist = null; // 임의 size 허용
    const AlignStyle = Quill.import("attributors/style/align");
    const FontStyle = Quill.import("attributors/style/font");
    FontStyle.whitelist = null;
    Quill.register(SizeStyle, true);
    Quill.register(AlignStyle, true);
    Quill.register(FontStyle, true);
  } catch (e) {
    console.error("Quill attributor register error:", e);
  }
  registered = true;
}

/* Delta ops 정규화: size 가 숫자면 "Npx" 로 변환 (style attributor가 적용되도록) */
function normalizeOps(ops) {
  if (!Array.isArray(ops)) return ops;
  return ops.map((op) => {
    if (op && op.attributes && typeof op.attributes.size === "number") {
      return {
        ...op,
        attributes: { ...op.attributes, size: `${op.attributes.size}px` },
      };
    }
    return op;
  });
}

/**
 * @param {{content: string|Array, className?: string}} props
 *  content: Quill Delta(JSON 문자열 또는 ops 배열) 또는 HTML 문자열
 */
export default function QuillContent({ content, className = "" }) {
  const hostRef = useRef(null);

  useEffect(() => {
    ensureRegistered();
    const host = hostRef.current;
    if (!host) return;

    host.innerHTML = "";
    const el = document.createElement("div");
    host.appendChild(el);

    const quill = new Quill(el, {
      readOnly: true,
      modules: { toolbar: false },
      theme: "snow",
    });

    // content 파싱: Delta(JSON 배열/{ops}) 우선, 실패 시 HTML 로 처리
    let ops = null;
    if (Array.isArray(content)) {
      ops = content;
    } else if (typeof content === "string") {
      try {
        const parsed = JSON.parse(content);
        if (Array.isArray(parsed)) ops = parsed;
        else if (parsed && Array.isArray(parsed.ops)) ops = parsed.ops;
      } catch (e) {
        ops = null;
      }
    }

    if (ops) {
      quill.setContents({ ops: normalizeOps(ops) });
    } else if (typeof content === "string" && content) {
      quill.clipboard.dangerouslyPasteHTML(content);
    }

    return () => {
      if (host) host.innerHTML = "";
    };
  }, [content]);

  return <div ref={hostRef} className={`kd-quill ${className}`} />;
}
